import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const TABLE_SESSION_STORAGE_KEY = 'table-session';

export interface TableSession {
  guestToken: string;
  restaurantUuid: string;
  // The QR token from the scanned URL. Kept so dine-in routes/links stay scoped
  // to /:restaurantUuid/table/:tableToken (distinct from the online-ordering URL).
  tableToken: string;
  sessionUuid: string;
  // Fix 12: server sends CharField string; stored and displayed as string
  tableNumber: string | number;
  mode: 'ordering' | 'buffet';
  covers: number;
  roundCount: number;
  cooldownSecondsRemaining: number;
  expiresAt: string;
  // SC-169: restaurant opted into the shared table cart (from join / current response).
  sharedTableCartEnabled: boolean;
  // SC-169: the nickname this guest entered at join time. Kept client-side so the
  // shared cart can flag this guest's own lines ("你添加的") and stamp added_by on
  // shared-cart adds. Optional: older persisted sessions predate it.
  nickname?: string;
  // Waiter handheld mode (spec §3.1): set when this session was opened/joined
  // from /:restaurantUuid/serveur rather than a scanned guest QR — drives
  // RestaurantShell's WaiterBar. Optional: customer/older sessions predate it.
  waiterMode?: boolean;
  waiterName?: string;
}

interface TableSessionState {
  session: TableSession | null;
}

const initialState: TableSessionState = {
  session: null,
};

function sessionStorageKey(restaurantUuid: string): string {
  return `${TABLE_SESSION_STORAGE_KEY}:${restaurantUuid}`;
}

// DF-0259: the guest token lives in localStorage, not sessionStorage. Scanning a
// QR from the camera app (iOS) or Lens (Android) opens a NEW TAB, where
// sessionStorage is empty: the rejoin then went out without a token and the
// server created a second TableGuest for the same phone. The customer's own
// dishes moved under "Convive 1" while they became "Convive 2 (vous)", and the
// ghost devices ate the 10-device cap (one Yuji table hit 10 devices for 7
// covers). The server already rejoins idempotently when handed the token
// (sale_api services.py, "Idempotent rejoin (rev 2)") — only the client lost it.
// Expiry is still enforced on read, and clearTableSession still wipes the entry.
function persistToStorage(session: TableSession): void {
  try {
    localStorage.setItem(sessionStorageKey(session.restaurantUuid), JSON.stringify(session));
  } catch {
    // localStorage unavailable (e.g. private mode with storage blocked)
  }
}

function removeFromStorage(restaurantUuid: string): void {
  try {
    localStorage.removeItem(sessionStorageKey(restaurantUuid));
    // Older builds wrote the same key to sessionStorage — clear both, or a
    // cleared session would come back on the next read of this tab.
    sessionStorage.removeItem(sessionStorageKey(restaurantUuid));
  } catch {
    // storage unavailable
  }
}

function loadFromStorage(restaurantUuid: string): TableSession | null {
  try {
    // Read-through to sessionStorage for the tabs that were already open when
    // this build shipped: without it, every table mid-service at deploy time
    // loses its guest and re-joins as a new one.
    const raw =
      localStorage.getItem(sessionStorageKey(restaurantUuid)) ??
      sessionStorage.getItem(sessionStorageKey(restaurantUuid));
    if (!raw) return null;
    const parsed: TableSession = JSON.parse(raw);
    // Discard expired sessions
    if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) {
      removeFromStorage(restaurantUuid);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

const tableSessionSlice = createSlice({
  name: 'tableSession',
  initialState,
  reducers: {
    setTableSession(state, action: PayloadAction<TableSession>) {
      state.session = action.payload;
      persistToStorage(action.payload);
    },
    clearTableSession(state, action: PayloadAction<string>) {
      state.session = null;
      removeFromStorage(action.payload);
    },
    // Drop dine-in from the active store WITHOUT erasing storage. Used when the
    // URL leaves the table route (e.g. /:restaurantUuid online ordering) so a
    // lingering session can't turn a from-home order into a dine-in one — yet a
    // return to the /table/:tableToken URL rehydrates it.
    deactivateTableSession(state) {
      state.session = null;
    },
    rehydrateTableSession(state, action: PayloadAction<string>) {
      state.session = loadFromStorage(action.payload);
    },
    updateSessionState(state, action: PayloadAction<Pick<TableSession, 'roundCount' | 'cooldownSecondsRemaining'>>) {
      if (state.session) {
        state.session.roundCount = action.payload.roundCount;
        state.session.cooldownSecondsRemaining = action.payload.cooldownSecondsRemaining;
        persistToStorage(state.session);
      }
    },
    // SC-355: sync the live table number onto the active session after a
    // back-office table transfer (转台). The tableNumber captured at join goes
    // stale when staff move the session to another table; the 5s poll returns
    // the session's current table, so writing it back keeps every "Ma table"
    // surface (chip, menu banner, panel) on the active table. Historical order
    // data is untouched — only the session's own table pointer is updated.
    syncSessionTable(state, action: PayloadAction<string | number>) {
      if (state.session) {
        state.session.tableNumber = action.payload;
        persistToStorage(state.session);
      }
    },
  },
});

export const {
  setTableSession,
  clearTableSession,
  deactivateTableSession,
  rehydrateTableSession,
  updateSessionState,
  syncSessionTable,
} = tableSessionSlice.actions;

export const tableSessionReducer = tableSessionSlice.reducer;
export default tableSessionSlice.reducer;

// Selectors
type StateWithTableSession = { tableSession: TableSessionState };

export const selectTableSession = (state: StateWithTableSession): TableSession | null =>
  state.tableSession.session;

export const selectGuestToken = (state: StateWithTableSession): string | null =>
  state.tableSession.session?.guestToken ?? null;

export const selectIsInDineIn = (state: StateWithTableSession): boolean =>
  state.tableSession.session !== null;

// SC-218: dine-in session whose restaurant is in buffet mode. Drives the
// "Inclus" menu display and the 0-base pricing mirror for included products.
export const selectIsBuffetDineIn = (state: StateWithTableSession): boolean =>
  state.tableSession.session?.mode === 'buffet';

// SC-169 / SC-203: dine-in AND the restaurant enabled the shared table cart.
// All shared-cart UI/behaviour is gated on this — false ⇒ classic per-phone
// dine-in. Applies to BOTH ordering and buffet modes: the backend shared-cart
// endpoints are mode-agnostic, and the shared checkout submits through the
// round-aware /cart/submit/ path (price-less, so it fits buffet rounds). A
// buffet table with the flag on therefore shares one cart like any other —
// previously buffet was wrongly excluded, so its shared cart never merged (SC-203).
export const selectIsSharedTableCart = (state: StateWithTableSession): boolean =>
  state.tableSession.session !== null &&
  state.tableSession.session.sharedTableCartEnabled === true;
